import { Button, Loading } from "@lyfegen-platform/react-common";
import { Avatar, Box, Typography } from "@material-ui/core";
import AddAPhotoIcon from "@material-ui/icons/AddAPhoto";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import InfoDivider from "../../../components/InfoDivider";
import LyfeFormField from "../../../components/LyfeFormField";
import { bottomFixedDrawerState } from "../../../store/layout";
import { UserPersonalInfo } from "../../../store/user";
import { deepEqual } from "../../../utils/deep-equal";
import useStyles from "../../data/forms/forms-styles";
import AvatarUploadDialog from "./AvatarUploadDialog";
import Language from "./Language";
import ReadOnlyInput from "./ReadOnlyInput";
import ResetPassword from "./ResetPassword";
import schema from "./schema";
import Signature from "./Signature";
import usePersonalInfo from "./use-personal-info";

export const BottomDrawerUserButtons = ({ submitForm, resetForm, isValid, initialValues, values, isSubmitting }: any) => {
	const { t } = useTranslation();
	const changed = initialValues && values ? !deepEqual(initialValues, values) : false;

	return (
		<Box display="flex" justifyContent="space-between" width="100%">
			<Button onClick={resetForm} color="primary">
				{t("cancel")}
			</Button>
			<Button
				startIcon={isSubmitting && <Loading size="14px" color="inherit" />}
				variant="contained"
				color="primary"
				disabled={!isValid || !changed}
				onClick={submitForm}
			>
				{t("save").toUpperCase()}
			</Button>
		</Box>
	);
};

export const BottomDrawerUser = (props: any) => {
	const bottomDrawer = useSetRecoilState(bottomFixedDrawerState);
	const { submitForm, resetForm, isValid, initialValues, values, isSubmitting } = props;
	useEffect(() => {
		bottomDrawer(<BottomDrawerUserButtons {...props} />);
		return () => bottomDrawer(null);
	}, [submitForm, resetForm, isValid, initialValues, values, isSubmitting]);

	return null;
};

const PersonalTab = (props: { identityServerUrl: string; hideSignature: boolean }) => {
	const [showDialog, setShowDialog] = useState<boolean>(false);
	const openDialog = () => setShowDialog(true);
	const closeDialog = () => setShowDialog(false);
	const { t } = useTranslation();
	const [personalInfo, save] = usePersonalInfo();
	const [isUserImagePresent, setIsUserImagePresent] = useState<boolean>(!!personalInfo.userImg);
	const classes = useStyles();

	const saveForm = (values: UserPersonalInfo) => {
		save(values);
	};

	return (
		<Box paddingBottom={5}>
			<InfoDivider paddingX={5} paddingTop={3} title="personal-information" />
			<Formik
				initialValues={personalInfo}
				onSubmit={saveForm}
				validationSchema={schema}
				validateOnChange
				validateOnMount
				enableReinitialize
			>
				{({ initialValues, values, submitForm, resetForm, isValid, isSubmitting }) => {
					const bottomDrawerProps = {
						submitForm,
						resetForm,
						isValid,
						initialValues,
						values,
						isSubmitting,
					};

					return (
						<Box display="flex" flexDirection="column" width="100%">
							<Box display="flex" flexDirection="row" justifyContent="space-between" width="100%" margin={5}>
								<Box width="276px" paddingTop={2} display={"flex"} className={classes.userAvatarBox}>
									<Box className={classes.userAvatarClickArea} onClick={() => openDialog()}>
										<Avatar className={classes.userAvatar} src={personalInfo.userImg || ""} />
										<AddAPhotoIcon className={classes.addAPhotoIcon} />
									</Box>
									<Box>
										<Typography className={classes.userFullName}>
											{personalInfo.firstName} {personalInfo.lastName}
										</Typography>
										<Typography>{personalInfo.organization.name}</Typography>
										{!isUserImagePresent && showDialog && (
											<Typography className={classes.uploadText}>{t("upload-picture")}</Typography>
										)}
									</Box>
								</Box>
								<Box width="276px" paddingTop={4}>
									<ReadOnlyInput
										label={"last-logged-in"}
										value={
											personalInfo.previousLoginDate ? new Date(personalInfo.previousLoginDate).toUTCString() : "-"
										}
									/>
								</Box>
								<Box width="180px"></Box>
								<Box width="366px" paddingTop={4} paddingRight={12}>
									<Language />
								</Box>
								<AvatarUploadDialog
									isOpen={showDialog}
									onDiscardClick={closeDialog}
									setIsUserImagePresent={setIsUserImagePresent}
									isUserImagePresent={isUserImagePresent}
								/>
							</Box>
							<Box display="flex" flexDirection="row" justifyContent="space-between" width="100%" margin={5}>
								<Box width="276px">
									<LyfeFormField name="firstName" height={16} fullWidth label={t("first-name")} required />
								</Box>
								<Box width="276px">
									<LyfeFormField name="lastName" height={16} fullWidth label={t("last-name")} required />
								</Box>
								<Box width="276px">
									<ReadOnlyInput label={"email"} value={values.email} />
								</Box>
								<Box width="276px">
									<ResetPassword identityServerUrl={props.identityServerUrl} email={values.email} />
								</Box>
							</Box>
							<Box display="flex" flexDirection="row" justifyContent="space-between" width="100%" marginLeft={5}>
								<Box width="276px">
									<LyfeFormField name="department" height={16} fullWidth label={t("department")} />
								</Box>
								<Box width="276px">
									<LyfeFormField name="position" height={16} fullWidth label={t("position")} />
								</Box>
								<Box width="276px"></Box>
								<Box width="276px"></Box>
							</Box>

							<BottomDrawerUser {...bottomDrawerProps} />
						</Box>
					);
				}}
			</Formik>

			{!props.hideSignature && <Signature personalInfo={personalInfo} />}
		</Box>
	);
};

export default PersonalTab;
